import { z } from 'zod';

import { PROFILE_UPLOAD_EXC, PROFILE_DELETE_EXC } from './formats.js';

/**
 * `org.images` request/response schemas — `POST /user-profile-image` and
 * `DELETE /user-profile-image`.
 *
 * Derived from the images-backend executable contract
 * (`contracts-legacy/images-backend/schemas.ts`). Field names stay on the legacy
 * wire spelling; every object is `.strict()`.
 */

// ----------------------------------------------------------------	inputs ----

/** `POST /user-profile-image` request — the only field the handler reads. */
export const profileImageUploadInputSchema = z
  .object({
    /** Base64; data-URL prefix stripped and `=` padding added server-side. */
    image_data: z.string(),
  })
  .strict();
export type ProfileImageUploadInput = z.infer<typeof profileImageUploadInputSchema>;

/**
 * `DELETE /user-profile-image` request — the handler reads no request fields
 * (the target user is `authorizer.lambda.sub`, dossier §6.10).
 */
export const profileImageDeleteInputSchema = z.object({}).strict();
export type ProfileImageDeleteInput = z.infer<typeof profileImageDeleteInputSchema>;

// --------------------------------------------------------------- outputs ----

/**
 * `POST /user-profile-image` 200 — `image_url` is `data.get('public_url', '')`
 * from Clerk's JSON: any string, `''` when the key is ABSENT. Deliberately still
 * `z.string()` and NOT `.nullable()`: an explicit JSON `null` would be rejected —
 * whether Clerk ever sends one is an open residual (CLOSURE.md §4 / R14) this
 * issue INHERITS, not closes.
 */
export const profileImageUploadSuccessSchema = z
  .object({
    message: z.literal('Profile image updated'),
    image_url: z.string(),
  })
  .strict();

/** `POST /user-profile-image` 400 — two literals out of one validation prologue. */
export const profileImageUploadBadRequestSchema = z
  .object({
    error: z.enum(['image_data is required', 'Invalid base64 image data']),
  })
  .strict();

/**
 * `POST /user-profile-image` 403 — `provider != 'clerk'` or an empty `sub`
 * (dossier §6.10). The routes are Clerk-only; there is no org check.
 */
export const profileImageForbiddenSchema = z
  .object({ error: z.literal('Clerk authentication required') })
  .strict();

/**
 * The two 500 bodies on the upload path are STRUCTURALLY DISTINCT and must not
 * both match one body: the Clerk-non-200 branch returns the BARE literal while
 * the `except Exception` branch returns the message plus `': ' + str(e)`.
 */

/** `POST /user-profile-image` 500 — Clerk answered non-200 (bare, no suffix). */
export const profileImageUploadClerkFailedSchema = z
  .object({ error: z.literal('Failed to upload profile image') })
  .strict();

/** `POST /user-profile-image` 500 — exception escaping the Clerk call / `json.loads`. */
export const profileImageUploadExceptionSchema = z
  .object({ error: z.string().regex(PROFILE_UPLOAD_EXC) })
  .strict();

/** `POST /user-profile-image` response — union of every decoded handler body. */
export const profileImageUploadResponseSchema = z.union([
  profileImageUploadSuccessSchema,
  profileImageUploadBadRequestSchema,
  profileImageForbiddenSchema,
  profileImageUploadClerkFailedSchema,
  profileImageUploadExceptionSchema,
]);
export type ProfileImageUploadResponse = z.infer<typeof profileImageUploadResponseSchema>;

/** `DELETE /user-profile-image` 200. */
export const profileImageDeleteSuccessSchema = z
  .object({ message: z.literal('Profile image deleted') })
  .strict();

/** `DELETE /user-profile-image` 500 — Clerk answered non-200 (bare, no suffix). */
export const profileImageDeleteClerkFailedSchema = z
  .object({ error: z.literal('Failed to delete profile image') })
  .strict();

/** `DELETE /user-profile-image` 500 — exception escaping the Clerk call. */
export const profileImageDeleteExceptionSchema = z
  .object({ error: z.string().regex(PROFILE_DELETE_EXC) })
  .strict();

/** `DELETE /user-profile-image` response — union of every decoded handler body. */
export const profileImageDeleteResponseSchema = z.union([
  profileImageDeleteSuccessSchema,
  profileImageForbiddenSchema,
  profileImageDeleteClerkFailedSchema,
  profileImageDeleteExceptionSchema,
]);
export type ProfileImageDeleteResponse = z.infer<typeof profileImageDeleteResponseSchema>;
